import * as React from "react";
import { ChevronDown, Inbox, MoveRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { ContractFamilyDocumentViewModel, ContractFamilyGroupViewModel } from "../contractFamilyViewModels";

interface MoveTarget {
  familyId: string;
  parentDocumentId: string;
}

interface Props {
  document: ContractFamilyDocumentViewModel;
  families: ContractFamilyGroupViewModel[];
  pending: boolean;
  onMoveToFamily: (target: MoveTarget) => void;
  onMoveToUngrouped: () => void;
}

export const ContractFamilyMoveMenu: React.FC<Props> = ({
  document,
  families,
  pending,
  onMoveToFamily,
  onMoveToUngrouped,
}) => {
  if (document.role === "MASTER") {
    return null;
  }

  const targetFamilies = families.filter(
    family => family.familyId !== document.familyId && family.rootDocumentId !== null,
  );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button type="button" size="xs" variant="outline" disabled={pending}>
          <MoveRight className="mr-1 h-3.5 w-3.5" />
          Move
          <ChevronDown className="ml-1 h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {targetFamilies.map(family => (
          <DropdownMenuItem
            key={family.familyId}
            className="cursor-pointer"
            onSelect={() =>
              onMoveToFamily({
                familyId: family.familyId,
                parentDocumentId: family.rootDocumentId!,
              })
            }
          >
            <MoveRight className="mr-2 h-3.5 w-3.5 text-slate-500" />
            <span className="min-w-0 whitespace-normal break-words" title={family.name}>
              {family.name}
            </span>
          </DropdownMenuItem>
        ))}
        {targetFamilies.length > 0 && <DropdownMenuSeparator />}
        <DropdownMenuItem className="cursor-pointer" onSelect={onMoveToUngrouped}>
          <Inbox className="mr-2 h-3.5 w-3.5 text-slate-500" />
          Ungrouped
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
